import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { number, object } from 'yup';

import { useLogger } from 'Src/feature/context-logger/useLogger';
import { getTypedFormComponents } from 'Src/feature/form/getTypedFormComponents';
import { useForm } from 'Src/feature/form/useForm';
import { Modal } from 'Src/feature/modal/Modal';
import { ModalAction } from 'Src/feature/modal/ModalAction';
import { ModalActions } from 'Src/feature/modal/ModalActions';
import { ModalContent } from 'Src/feature/modal/ModalContent';
import { ModalHeader } from 'Src/feature/modal/ModalHeader';
import { ModalSubTitle } from 'Src/feature/modal/ModalSubTitle';
import { ModalTitle } from 'Src/feature/modal/ModalTitle';

import { MenuScreenButton } from './menuScreenService';

const sectionId = 'MenuItemOpenPriceModal';

interface MenuItemOpenPriceModalProps {
  open: boolean;
  button?: MenuScreenButton;
  onClose: () => void;
  onDone: (options: { button: MenuScreenButton }) => Promise<void>;
}

const { Form, Field } = getTypedFormComponents<{ price: number }>();

const MenuItemOpenPriceModalContent: FC<MenuItemOpenPriceModalProps> = ({ button, onClose, onDone }) => {
  const { t } = useTranslation();
  const log = useLogger().getSubLogger({ name: sectionId });

  const form = useForm<{ price: number }>({
    defaultValues: {
      price: undefined,
    },
    schema: object().shape({
      price: number().label(t('labels.price')).moreThan(0).required(),
    }),
  });

  const onSave = async () => {
    const { values, isValid } = await form.action.submit();
    if (!values) return log.info('onSave', '!values');
    if (!isValid) return log.info('onSave', 'form invalid');
    if (!button) return log.info('onSave', '!button');
    await onDone({ button: { ...button, price: parseFloat(values.price.toString()) } });
    onClose();
  };

  return (
    <>
      <ModalHeader>
        <ModalTitle copy={button?.menuItemCombinedName} />
        <ModalSubTitle copy={t('menuItem.labels.enterPrice')} />
      </ModalHeader>
      <ModalContent flex={{ justifyContent: 'center', alignItems: 'center' }}>
        <Form usedForm={form}>
          <Field type="currencyInput" name="price" />
        </Form>
      </ModalContent>
      <ModalActions>
        <ModalAction label={t('labels.cancel')} color="secondary" onClick={onClose} />
        <ModalAction label={t('labels.done')} color="primary" disabled={!form.state.isValid} onClick={onSave} />
      </ModalActions>
    </>
  );
};

// eslint-disable-next-line react/no-multi-comp
const MenuItemOpenPriceModal: FC<MenuItemOpenPriceModalProps> = ({ open, button, onClose, onDone }) => (
  <Modal
    data-testid={sectionId}
    open={open}
    onBackDropClick={onClose}
    variant="grow"
  >
    <MenuItemOpenPriceModalContent open={open} button={button} onClose={onClose} onDone={onDone} />
  </Modal>
);

export { MenuItemOpenPriceModal };
